import {
  WSActions,
  WSAlert,
  WSCard,
  WSElement,
  WSForm,
  WSGrid,
  WSGridItem,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import isEmpty from "lodash/isEmpty";
import React from "react";
import * as Yup from "yup";
import { ObjectSchema } from "yup";

import { WSQueries } from "../../../../query/WSQuery";
import { useQueryRequirementDefinition } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinition";
import { DebugData } from "../../../../shared/components/DebugData/DebugData";
import { StepComponentProps } from "../../../../shared/components/ModalFlow/types";
import { IS_PRODUCTION_ENV } from "../../../../shared/constants/environment";
import { getChangedData } from "../../../../utils/getChangedData";
import { useRequirementDefinitionSettings } from "../../hooks/useRequirementDefinitionSettings";
import { getBaseEditValues } from "../../utils/getBaseEditValues";
import { getRequirementDefinitionTemplate } from "../../utils/getRequirementDefinitionTemplate";
import {
  requirementDefinitionBaseFieldsValidationSchema,
  settingsFieldsValidationSchema
} from "../CreateRequirementDefinitionForm/validationSchema";
import { FormSectionApprovals } from "../FormSectionApprovals";
import { FormSectionExpiration } from "../FormSectionExpiration";

import {
  FlowCreateNewVersionSettingsData,
  FlowCreateNewVersionState
} from "./types";
import { DataItem } from "../../../../shared/components/DataItem";

export const StepConfigureSettings: React.FC<
  StepComponentProps<FlowCreateNewVersionState>
> = ({ onClose, onNext, setState, state }) => {
  const queryRequirementDefinition = useQueryRequirementDefinition(
    state.requirementDefinitionId
  );

  const querySettingsItem = useRequirementDefinitionSettings({
    dataSourceId: queryRequirementDefinition.data?.dataSourceId,
    requirementType: queryRequirementDefinition.data?.requirementType
  });

  return (
    <WSQueries queries={{ queryRequirementDefinition, querySettingsItem }}>
      {({
        queryRequirementDefinitionData: requirementDefinition,
        querySettingsItemData
      }) => {
        const template = getRequirementDefinitionTemplate(
          requirementDefinition,
          querySettingsItemData
        );

        const noExpirationForm = !!template.noExpirationForm;

        const initialValues: FlowCreateNewVersionSettingsData = (() => {
          const { type, ...requirementValues } = getBaseEditValues(
            requirementDefinition
          );

          const settingsValues = template.form?.getEditSettingsValues(
            template,
            requirementDefinition,
            querySettingsItemData
          );

          return {
            ...requirementValues,
            ...settingsValues
          };
        })();

        const defaultValues = state.settingsData ?? initialValues;

        const validationSchema = (() => {
          const validations = template.form?.validationSchema
            ? template.form?.validationSchema
            : Yup.object().shape({});

          return settingsFieldsValidationSchema({
            noExpirationForm: !!template.noExpirationForm
          })
            .concat(validations as ObjectSchema<any>)
            .concat(requirementDefinitionBaseFieldsValidationSchema);
        })();

        const partialForm = template.form?.partialForm;

        const handleSubmit = (
          settingsData: FlowCreateNewVersionSettingsData
        ): void => {
          const changedData = getChangedData(initialValues, settingsData);

          setState({ changedData, settingsData });
          onNext();
        };

        const isNameEditable = !!template.isCustomName;

        return (
          <WSForm<FlowCreateNewVersionSettingsData>
            defaultValues={defaultValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <WSList gap="2XL">
              <WSElement>
                <WSText.Heading4>Configure settings</WSText.Heading4>
                <WSText.ParagraphSm color="gray500" mt="XS">
                  Review and configure the settings for the new version of this
                  requirement.
                </WSText.ParagraphSm>
              </WSElement>

              <WSCard header="Requirement information">
                <WSList gap="M">
                  <WSGrid gutter="XL">
                    <WSGrid.Item span={{ m: "6", s: "12" }}>
                      <WSForm.Field
                        component={WSInputText}
                        label="Name"
                        componentProps={{
                          disabled: !isNameEditable,
                          required: true,
                          helperText:
                            "The requirement name is visible to you and contractors",
                          pill: {
                            icon: "view",
                            text: "Public",
                            theme: "neutral"
                          }
                        }}
                        name="name"
                        placeholder="Requirement name"
                      />
                    </WSGrid.Item>
                    <WSGrid.Item span={{ m: "6", s: "12" }}>
                      <DataItem
                        label="Type"
                        icon={template.icon}
                        value={template.name}
                      />
                    </WSGrid.Item>
                    <WSGrid.Item span={{ m: "6", s: "12" }}>
                      <DataItem
                        label="Version"
                        value={`v${requirementDefinition.version + 1}`}
                      />
                    </WSGrid.Item>
                  </WSGrid>

                  <WSForm.Field
                    component={WSInputTextarea}
                    componentProps={{
                      required: false,
                      pill: {
                        icon: "lock",
                        text: "Private",
                        theme: "neutral"
                      },
                      helperText:
                        "The description is visible internally to you and your team only"
                    }}
                    label="Description"
                    name="description"
                    placeholder="Add optional description for this requirement"
                  />
                </WSList>
              </WSCard>

              {partialForm}

              <FormSectionExpiration noExpirationForm={noExpirationForm} />

              <FormSectionApprovals
                isRequirementWithErrorStates={
                  template.isRequirementWithErrorStates
                }
              />

              <WSForm.Data<FlowCreateNewVersionSettingsData>>
                {formData => {
                  const changedData = getChangedData(initialValues, formData, {
                    strict: false
                  });

                  const { name, description, ...settingsChangedData } =
                    changedData;

                  const isSettingsUnchanged = isEmpty(settingsChangedData);

                  return (
                    <>
                      {IS_PRODUCTION_ENV ? null : (
                        <>
                          <WSList.Horizontal equalItems>
                            <DebugData
                              data={initialValues}
                              title="initial values:"
                            />
                            <DebugData data={formData} title="form values:" />
                          </WSList.Horizontal>
                          <DebugData data={changedData} title="diff:" />
                        </>
                      )}

                      {isSettingsUnchanged ? (
                        <WSAlert
                          icon="info-circle"
                          theme="info"
                          title="No settings changes"
                        >
                          It appears no settings were changed. If you only wish
                          to change the display name or description but keep all
                          settings the same, go back to edit the requirement
                          instead of creating a new version.
                        </WSAlert>
                      ) : null}
                      <WSActions
                        alignment="fill"
                        buttons={[
                          {
                            kind: "Secondary",
                            label: "Cancel",
                            onClick: onClose,
                            type: "button"
                          },
                          {
                            disabled: isSettingsUnchanged,
                            kind: "Primary",
                            label: "Save & continue",
                            type: "submit"
                          }
                        ]}
                      />
                    </>
                  );
                }}
              </WSForm.Data>
            </WSList>
          </WSForm>
        );
      }}
    </WSQueries>
  );
};
